import { useState } from 'react'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { useConsultationStore } from '../../../../stores/consultationStore'
import { Trash2, Download, Upload, Edit2, Check, X, MessageSquare, FileText, Copy } from 'lucide-react'

export function SessionManager() {
  const consultationStore = useConsultationStore()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingTitle, setEditingTitle] = useState('')
  
  const handleStartEdit = (sessionId: string, currentTitle: string) => {
    setEditingId(sessionId)
    setEditingTitle(currentTitle)
  }
  
  const handleSaveEdit = (sessionId: string) => {
    if (editingTitle.trim()) {
      consultationStore.updateSessionTitle(sessionId, editingTitle.trim())
    }
    setEditingId(null)
    setEditingTitle('')
  }
  
  const handleCancelEdit = () => {
    setEditingId(null)
    setEditingTitle('')
  }
  
  const handleExportSession = (sessionId: string) => {
    consultationStore.exportSession(sessionId)
  }
  
  const handleExportAll = () => {
    const json = consultationStore.exportAllSessions()
    const blob = new Blob([json], { type: 'application/json;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `consultation_sessions_${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }
  
  const handleImport = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.json'
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) {
        const text = await file.text()
        try {
          consultationStore.importSessions(text)
        } catch (error) {
          alert('インポートに失敗しました。ファイル形式を確認してください。')
        }
      }
    }
    input.click()
  }
  
  const handleDuplicateSession = (sessionId: string) => {
    const duplicated = consultationStore.duplicateSession(sessionId)
    if (duplicated) {
      alert(`セッション「${duplicated.title}」を作成しました`)
    } else {
      alert('セッションの複製に失敗しました')
    }
  }

  const handleDeleteSession = (sessionId: string) => {
    if (confirm('このセッションを削除してもよろしいですか？')) {
      consultationStore.deleteSession(sessionId)
    }
  }
  
  const sessions = consultationStore.sessions
  const sortedSessions = [...sessions].sort((a, b) => 
    new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  )
  
  return (
    <div className="space-y-4">
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">保存済みセッション</h3>
          <div className="space-x-2">
            <Button 
              size="sm" 
              variant="outline"
              onClick={handleImport}
            >
              <Upload className="h-4 w-4 mr-1" />
              インポート
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              onClick={handleExportAll}
              disabled={sessions.length === 0}
            >
              <Download className="h-4 w-4 mr-1" />
              すべてエクスポート
            </Button>
          </div>
        </div>
        
        {sessions.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            保存済みセッションがありません
          </div>
        ) : (
          <div className="space-y-2">
            {sortedSessions.map((session) => {
              const isActive = consultationStore.getCurrentActiveSession()?.id === session.id
              const messageCount = session.messages.filter(m => m.role !== 'system').length
              
              return (
                <div 
                  key={session.id}
                  className={`border rounded-lg p-3 ${isActive ? 'bg-muted' : ''}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {editingId === session.id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            value={editingTitle}
                            onChange={(e) => setEditingTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveEdit(session.id)
                              } else if (e.key === 'Escape') {
                                handleCancelEdit()
                              }
                            }}
                            className="h-8"
                            autoFocus
                          />
                          <Button 
                            size="icon" 
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => handleSaveEdit(session.id)}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="icon" 
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={handleCancelEdit}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div 
                          className="cursor-pointer hover:underline"
                          onClick={() => consultationStore.loadSession(session.id)}
                        >
                          <h4 className="font-medium">{session.title}</h4>
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground mt-1">
                        <span className="inline-flex items-center gap-1">
                          <MessageSquare className="h-3 w-3" />
                          {messageCount} メッセージ
                        </span>
                        <span className="mx-2">•</span>
                        <span>更新: {new Date(session.updatedAt).toLocaleString('ja-JP')}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1">
                      {!editingId && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => handleStartEdit(session.id, session.title)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => handleDuplicateSession(session.id)}
                        title="セッションを複製"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => handleExportSession(session.id)}
                        title="JSONでエクスポート（インポート可能）"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => consultationStore.exportSession(session.id, 'text')}
                        title="テキスト形式でエクスポート（読みやすい形式）"
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => handleDeleteSession(session.id)}
                        title="セッションを削除"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {isActive && (
                    <div className="mt-2 text-xs text-primary">
                      現在のセッション
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
      
      <div className="card">
        <h3 className="text-lg font-semibold mb-2">統計情報</h3>
        <div className="space-y-1 text-sm">
          <div>総セッション数: {sessions.length} / 50</div>
          <div>総メッセージ数: {consultationStore.countTotalMessages()}</div>
          <div>最終更新: {
            sessions.length > 0 
              ? new Date(sortedSessions[0].updatedAt).toLocaleString('ja-JP')
              : 'なし'
          }</div>
        </div>
      </div>
    </div>
  )
}